import { useState } from 'react';
import { css } from '@emotion/css';
import { Alert } from '@spark-web/alert';
import { Box } from '@spark-web/box';
import { Inline } from '@spark-web/inline';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Spinner } from '@spark-web/spinner';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { INCOME_VERIFICATION, LABELS } from '@utils';
import GreenCheckIcon from '../../../assets/green-check.svg?react';

const VIDEO_SOURCE_URL = 'https://static-assets.prod.cloud.brighte.com.au/marketplace/interstitial-modal-animation.mp4';

interface OpenBankingInstructionModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onToggle: () => void;
  onConnect: () => void;
  isCdrChallenger?: boolean;
}

export function OpenBankingInstructionModal({
  isOpen,
  isLoading,
  onToggle,
  onConnect,
  isCdrChallenger,
}: OpenBankingInstructionModalProps) {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  const {
    OPEN_BANKING_INSTRUCTION: {
      HEADER,
      STEP_ONE,
      STEP_TWO,
      STEP_THREE,
      ACTION,
      LOADING,
      VIDEO_UNAVAILABLE,
      LOADING_VIDEO,
      ACCOUNT_INFORMATION,
    },
    OPEN_BANKING_INSTRUCTION_CHALLENGER,
  } = INCOME_VERIFICATION;

  const header = isCdrChallenger ? OPEN_BANKING_INSTRUCTION_CHALLENGER.HEADER : HEADER;
  const stepOne = isCdrChallenger ? OPEN_BANKING_INSTRUCTION_CHALLENGER.STEP_ONE : STEP_ONE;
  const stepTwo = isCdrChallenger ? OPEN_BANKING_INSTRUCTION_CHALLENGER.STEP_TWO : STEP_TWO;
  const stepThree = isCdrChallenger ? OPEN_BANKING_INSTRUCTION_CHALLENGER.STEP_THREE : STEP_THREE;

  const accountInformation = isCdrChallenger
    ? OPEN_BANKING_INSTRUCTION_CHALLENGER.ACCOUNT_INFORMATION
    : ACCOUNT_INFORMATION;

  const handleToggle = () => {
    setVideoLoaded(false);
    setVideoError(false);
    onToggle();
  };

  return (
    <ContentDialog
      title={header}
      isOpen={isOpen}
      onToggle={handleToggle}
      footer={
        <Box
          as="button"
          type="button"
          onClick={onConnect}
          data={{ testid: 'open-banking-instruction-btn' }}
          background="primary"
          borderRadius={4}
          paddingX="medium"
          paddingY="large"
          cursor="pointer"
          marginY="medium"
          width={{ mobile: 'full', desktop: 'none' }}
          disabled={isLoading}
          css={{ minWidth: '120px' }}
        >
          {isLoading ? (
            <Inline gap="small" alignY="center" align="center" padding="none">
              <Spinner size="xxsmall" />
              <Text weight="semibold" size="small">
                {LOADING}
              </Text>
            </Inline>
          ) : (
            <Text weight="semibold" size="small">
              {isCdrChallenger ? LABELS.CONTINUE : ACTION}
            </Text>
          )}
        </Box>
      }
    >
      <Stack gap="xlarge">
        <Box display="flex" justifyContent="center" alignItems="center" position="relative">
          {(!videoLoaded || videoError) && (
            <Box
              background="neutral"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius={8}
              width="full"
              css={{
                maxWidth: '526px',
                aspectRatio: '16 / 9',
              }}
            >
              <Text size="small" tone="muted">
                {videoError ? VIDEO_UNAVAILABLE : LOADING_VIDEO}
              </Text>
            </Box>
          )}
          <video
            width="100%"
            style={{
              maxWidth: '526px',
              borderRadius: '8px',
              display: videoLoaded && !videoError ? 'block' : 'none',
            }}
            playsInline
            controls={false}
            autoPlay
            loop
            muted
            onLoadedData={() => setVideoLoaded(true)}
            onError={() => setVideoError(true)}
          >
            <source src={VIDEO_SOURCE_URL} type="video/mp4" />
          </video>
        </Box>

        <Stack gap="medium">
          <Box display="flex" justifyContent="spaceBetween" width="full">
            <Box
              padding="small"
              display="flex"
              alignItems="center"
              background="primaryLow"
              flex={1}
              css={{
                borderTopLeftRadius: '24px',
                borderBottomLeftRadius: '24px',
              }}
            >
              <Box
                background="primary"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                css={{
                  width: '28px',
                  height: '28px',
                }}
              >
                <Text weight="semibold" size="small">
                  1
                </Text>
              </Box>
            </Box>

            <Box
              padding="small"
              display="flex"
              alignItems="center"
              background="primaryLow"
              justifyContent="center"
              flex={1}
            >
              <Box
                background="primary"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                css={{
                  width: '28px',
                  height: '28px',
                }}
              >
                <Text weight="semibold" size="small">
                  2
                </Text>
              </Box>
            </Box>

            <Box
              padding="small"
              display="flex"
              justifyContent="end"
              background="primaryLow"
              flex={1}
              css={{
                borderTopRightRadius: '24px',
                borderBottomRightRadius: '24px',
              }}
            >
              <Box
                background="primary"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                css={{
                  width: '28px',
                  height: '28px',
                }}
              >
                <GreenCheckIcon />
              </Box>
            </Box>
          </Box>

          <Box display="flex" justifyContent="spaceBetween" gap="xxxlarge">
            <Box flex={1}>
              <Text align="left" size="small" tone="muted" css={{ maxWidth: '130px' }}>
                {stepOne}
              </Text>
            </Box>

            <Box flex={1}>
              <Text align="center" size="small" tone="muted">
                {stepTwo}
              </Text>
            </Box>

            <Box flex={1}>
              <Text align="right" size="small" tone="muted">
                {stepThree}
              </Text>
            </Box>
          </Box>
        </Stack>
        <Box marginTop="large">
          <Alert tone="info" heading="">
            <Text size="standard">{accountInformation}</Text>
          </Alert>
        </Box>
      </Stack>
    </ContentDialog>
  );
}
